import React from 'react';
import { MdMenuBook } from 'react-icons/md';

const ReferencePanel = () => {
  return (
    <div className="space-y-4 max-w-full">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Quick Reference</h2>
        <MdMenuBook className="w-6 h-6 text-gray-600" />
      </div>

      {/* Responsive Grid Layout - Adapts to container width */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        
        {/* Attribute Tags Reference */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 min-w-0">
          <h3 className="font-semibold text-blue-800 mb-2 text-sm">Attribute Types</h3>
          <div className="space-y-1 text-xs">
            <div className="flex items-center flex-wrap">
              <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@key</span>
              <span className="break-words">Unique identifier</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@partial_key</span>
              <span className="break-words">For weak entities</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@multivalued</span>
              <span className="break-words">Multiple values</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@derived</span>
              <span className="break-words">Calculated</span>
            </div>
          </div>
        </div>

        {/* Entity & Relationship Types */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 min-w-0">
          <h3 className="font-semibold text-green-800 mb-2 text-sm">Entity & Relationship Types</h3>
          <div className="space-y-1 text-xs">
            <div className="flex items-center flex-wrap">
              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@weak</span>
              <span className="break-words">Weak entity</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@identifying</span>
              <span className="break-words">Identifying relationship</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@total_participation</span>
              <span className="break-words">Must participate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Grid for Cardinality and Specialization */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Cardinality */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 min-w-0">
          <h3 className="font-semibold text-purple-800 mb-2 text-sm">Cardinality</h3>
          <div className="space-y-1 text-xs">
            <div><strong>1</strong> - Exactly one</div>
            <div><strong>N</strong> - Zero or more</div>
            <div><strong>M</strong> - Many (alternative)</div>
          </div>
        </div>

        {/* Specialization & Union Types */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 min-w-0">
          <h3 className="font-semibold text-teal-800 mb-2 text-sm">Specialization & Union</h3>
          <div className="space-y-1 text-xs">
            <div className="flex items-center flex-wrap">
              <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@disjoint</span>
              <span className="break-words">Exclusive categories</span>
            </div>
            <div className="flex items-center flex-wrap">
              <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs font-mono mr-2 mb-1">@overlapping</span>
              <span className="break-words">Multiple allowed</span>
            </div>
            <div className="text-gray-600 mt-1 break-words">
              <strong>Union:</strong> Multiple entities → Single entity
            </div>
          </div>
        </div>
      </div>

      {/* Compact Examples Section - Full width when narrow */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <h3 className="font-semibold text-gray-800 mb-2 text-sm">Quick Examples</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-xs">
          <div className="min-w-0">
            <strong className="text-blue-600">Composite:</strong>
            <div className="bg-white border rounded p-1 font-mono text-xs mt-1 overflow-x-auto">
              Address (street,city,state)
            </div>
          </div>
          
          <div className="min-w-0">
            <strong className="text-green-600">Key:</strong>
            <div className="bg-white border rounded p-1 font-mono text-xs mt-1 overflow-x-auto">
              StudentID @key
            </div>
          </div>

          <div className="min-w-0">
            <strong className="text-purple-600">Weak Entity:</strong>
            <div className="bg-white border rounded p-1 font-mono text-xs mt-1 overflow-x-auto">
              Dependent @weak
            </div>
          </div>

          <div className="min-w-0">
            <strong className="text-orange-600">Identifying:</strong>
            <div className="bg-white border rounded p-1 font-mono text-xs mt-1 overflow-x-auto">
              Has @identifying
            </div>
          </div>

          <div className="min-w-0">
            <strong className="text-teal-600">Union:</strong>
            <div className="bg-white border rounded p-1 font-mono text-xs mt-1 overflow-x-auto">
              Payable ← Room, Medication
            </div>
          </div>
        </div>
      </div>

      {/* Common Patterns - Condensed and Responsive */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-3">
        <h3 className="font-semibold text-blue-800 mb-2 text-sm">Common Patterns</h3>
        <div className="space-y-2 text-xs">
          <div className="bg-white rounded p-2 min-w-0">
            <strong>1:N:</strong> 
            <div className="font-mono text-xs mt-1 overflow-x-auto">
              Department (1) ← works_for → Employee (N)
            </div>
          </div>
          <div className="bg-white rounded p-2 min-w-0">
            <strong>M:N:</strong> 
            <div className="font-mono text-xs mt-1 overflow-x-auto">
              Student (N) ← enrolls → Course (M)
            </div>
          </div>
          <div className="bg-white rounded p-2 min-w-0">
            <strong>Weak:</strong> 
            <div className="font-mono text-xs mt-1 overflow-x-auto">
              Employee (1) ← Has @identifying → Dependent (N) @total_participation
            </div>
          </div>
        </div>
      </div>

      {/* Best Practices - Compact and Responsive */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <h3 className="font-semibold text-yellow-800 mb-2 text-sm">Quick Tips</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className="min-w-0">
            <h4 className="font-medium text-green-600 mb-1">✅ Do</h4>
            <ul className="space-y-0.5 text-gray-700">
              <li>• Use descriptive names</li>
              <li>• Use underscore for spaces</li>
              <li>• Be consistent</li>
            </ul>
          </div>
          
          <div className="min-w-0">
            <h4 className="font-medium text-red-600 mb-1">❌ Don't</h4>
            <ul className="space-y-0.5 text-gray-700">
              <li>• Use spaces in names</li>
              <li>• Mix naming styles</li>
              <li>• Forget attribute sections</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferencePanel;